import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import { StyledTabs } from "core/components/StyledTabs";
import { type ReactNode, type SyntheticEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const MENU_TAB_VALUE = "menu";

export interface LanguageTabsProps<T extends string | number = string> {
  handleTabChange: (event: SyntheticEvent, value: T) => void;
  isTabDefault?: boolean;
  currentTabs: { label: string; value: T }[];
  tabValue: T;
  unselectedTabs: { label: string; value: T }[];
  removeTabOnClick: (tabValue: T) => void;
  selectAddTabValue: (tabValue: T) => void;
  tabVariant?: "styled" | "standard";
  showAddTab?: boolean;
  showRemoveTab?: boolean;
  actionsSlot?: ReactNode;
}

export function LanguageTabs<T extends string>({
  handleTabChange,
  tabValue,
  isTabDefault,
  currentTabs,
  unselectedTabs,
  removeTabOnClick,
  selectAddTabValue,
  tabVariant = "styled",
  showAddTab = true,
  showRemoveTab = true,
  actionsSlot: actions,
}: LanguageTabsProps<T>) {
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("Fixhub");
  const removeTabVisible =
    !isTabDefault && currentTabs.length > 1 && Boolean(tabValue);
  const removeLabel = t(
    "Next:Core:LanguageTabs.RemoveLanguage",
    "Remove language"
  );
  const TabsComponent = tabVariant === "standard" ? Tabs : StyledTabs;
  return (
    <Box width="100%">
      <Menu
        id="additional-language-menu"
        // eslint-disable-next-line react-hooks/refs
        anchorEl={ref.current}
        open={menuOpen}
        onClose={() => {
          setMenuOpen(false);
        }}
      >
        {unselectedTabs.map(({ label, value }) => (
          <MenuItem
            key={value}
            onClick={(e) => {
              selectAddTabValue(value);
              setMenuOpen(false);
              handleTabChange(e, value);
            }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
      <Stack direction="row">
        <TabsComponent
          value={tabValue}
          onChange={(event, value) => {
            if (value === MENU_TAB_VALUE) {
              setMenuOpen(true);
              return;
            }
            handleTabChange(event, value);
          }}
        >
          {currentTabs.map(({ label, value }) => (
            <Tab key={value} label={label} value={value} />
          ))}
          {showAddTab && unselectedTabs.length > 0 && (
            <Tab
              value={MENU_TAB_VALUE}
              ref={ref}
              label={<AddIcon />}
              sx={{
                borderRadius: ({ shape: { borderRadius: br } }) =>
                  `${br}px ${br}px 0 0`,
                "&:hover": {
                  backgroundColor: ({ palette }) => palette.action.hover,
                },
              }}
            />
          )}
        </TabsComponent>
        <Stack
          direction="row"
          flex={1}
          justifyContent="flex-end"
          alignItems="center"
          gap={0.5}
        >
          {actions}
          {showRemoveTab && (
            <Tooltip title={removeLabel}>
              <span>
                <IconButton
                  color="error"
                  aria-label={removeLabel}
                  disabled={!removeTabVisible}
                  onClick={() => removeTabOnClick(tabValue)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      <Divider />
    </Box>
  );
}
